import { useHead } from '@unhead/vue'

const SITE_URL = 'https://getnerdcrm.com'
const SITE_NAME = 'NerdCRM'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og/default.png`

export interface SeoOptions {
  title: string
  description: string
  path?: string
  ogImage?: string
  ogType?: 'website' | 'article' | 'product'
  noIndex?: boolean
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>
  publishedTime?: string
  modifiedTime?: string
  author?: string
  /** If true, use `title` verbatim (no template). Default false: appends ` | NerdCRM`. */
  rawTitle?: boolean
}

// Short suffix: Google shows ~60 characters, so the page's own title must carry the keywords.
const titleTemplate = (page: string) => `${page} | ${SITE_NAME}`

export function useSeo(opts: SeoOptions) {
  const url = opts.path ? `${SITE_URL}${opts.path}` : SITE_URL
  const ogImage = opts.ogImage || DEFAULT_OG_IMAGE
  const ogType = opts.ogType || 'website'
  const fullTitle = opts.rawTitle ? opts.title : titleTemplate(opts.title)

  const meta: Array<Record<string, string>> = [
    { name: 'description', content: opts.description },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:type', content: ogType },
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: opts.description },
    { property: 'og:url', content: url },
    { property: 'og:image', content: ogImage },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:locale', content: 'en_US' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: opts.description },
    { name: 'twitter:image', content: ogImage },
  ]

  if (opts.noIndex) meta.push({ name: 'robots', content: 'noindex, nofollow' })
  if (opts.publishedTime) meta.push({ property: 'article:published_time', content: opts.publishedTime })
  if (opts.modifiedTime) meta.push({ property: 'article:modified_time', content: opts.modifiedTime })
  if (opts.author && ogType === 'article') meta.push({ property: 'article:author', content: opts.author })

  const script: Array<Record<string, string>> = []
  if (opts.jsonLd) {
    const blocks = Array.isArray(opts.jsonLd) ? opts.jsonLd : [opts.jsonLd]
    for (const block of blocks) {
      script.push({ type: 'application/ld+json', innerHTML: JSON.stringify(block) })
    }
  }

  useHead({
    title: fullTitle,
    link: [{ rel: 'canonical', href: url }],
    meta,
    script,
  })
}

export const seoConstants = {
  SITE_URL,
  SITE_NAME,
  DEFAULT_OG_IMAGE,
}
